import React from 'react';

import {
  ELocationSearchAutocompleteListVariant,
  LocationSearchAutocompleteList,
} from '@/components/common/LocationSearchAutocompleteList';
import { calculateOptionIndex, IGroupOption, IOption } from '@/utility/autoCompleteGroupedOptions';

// The autocomplete's own option click handler, which is what keyboard selection
// reuses. Named rather than written inline so the cast below stays on one line:
// prettier versions disagree on how to wrap a union type annotation.
type TOptionClickHandler = (event: React.SyntheticEvent) => void;

interface ILocationSearchContentProps {
  open: boolean;
  groupOptions: IGroupOption[];
  allGroupOptions: IGroupOption[];
  getListboxProps: () => Record<string, unknown>;
  getOptionProps: ({
    option,
    index,
  }: {
    option: IOption;
    index: number;
  }) => Record<string, unknown>;
  // Key handling on a suggestion that this component has no answer for, owned by
  // the parent: Tab at either end of the list leaves for the field the popup belongs
  // to, or for the rest of the search bar.
  onOptionKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
}

const LocationSearchContent: React.FC<ILocationSearchContentProps> = ({
  open,
  groupOptions,
  allGroupOptions,
  getListboxProps,
  getOptionProps,
  onOptionKeyDown,
}) => {
  return (
    <div
      hidden={!open}
      className={`w-[23rem] h-fit ${open ? 'block' : 'hidden'}`}
      {...getListboxProps()}>
      <LocationSearchAutocompleteList
        groupedOptions={groupOptions}
        getOptionProps={({ option, index }) => {
          const optionProps = getOptionProps({
            option,
            index: calculateOptionIndex(option.groupName, index, allGroupOptions),
          });

          // The autocomplete hands out `tabIndex: -1`, which assumes focus stays in
          // the input and the active option is tracked with aria-activedescendant.
          // Here focus moves into the list instead, so each suggestion is its own
          // Tab stop and Enter or Space picks the focused one, exactly as a click
          // does — the autocomplete's own click handler reads the option back off
          // the element, so it takes a keyboard event just as happily.
          const selectOption = optionProps.onClick as TOptionClickHandler | undefined;

          return {
            ...optionProps,
            tabIndex: 0,
            onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                selectOption?.(event);
                return;
              }

              onOptionKeyDown?.(event);
            },
          };
        }}
        variant={ELocationSearchAutocompleteListVariant.Vertical}
        className="!pt-0"
      />
    </div>
  );
};

export default LocationSearchContent;
